import { useCallback } from "react";
import { useSelector } from "react-redux";

import { useThunkDispatch } from "@/redux/dispatcher";
import {
    addDiscriminator,
    removeDiscriminator
} from "@/redux/modules/value/scrollLock/scrollLock.actions";
import { isLockedFor } from "@/redux/modules/value/scrollLock/scrollLock.selectors";

const SCROLL_LOCK_CLASS = "scroll-lock";

const useScrollLock = (discriminator: string) => {
    const dispatch = useThunkDispatch();

    const lockedForDiscriminator = useSelector(isLockedFor(discriminator));

    const isLocked = useCallback(
        () => document.body.classList.contains(SCROLL_LOCK_CLASS),
        []
    );

    const isLockedForDiscriminator = useCallback(
        () => lockedForDiscriminator,
        [lockedForDiscriminator]
    );

    const lock = useCallback(() => {
        dispatch(addDiscriminator(discriminator)).then(() => {
            const scrollbarWidth =
                window.innerWidth - document.body.clientWidth;

            const offsetY = window.scrollY;

            document.body.style.paddingRight = `${scrollbarWidth}px`;
            document.body.style.top = `${-offsetY}px`;
            document.body.classList.add(SCROLL_LOCK_CLASS);
        });
    }, [discriminator, dispatch]);

    const unlock = useCallback(() => {
        dispatch(removeDiscriminator(discriminator)).then((discriminators) => {
            if (!discriminators?.length && isLocked()) {
                const offsetY = Math.abs(
                    parseInt(document.body.style.top || "0", 10)
                );

                document.body.classList.remove(SCROLL_LOCK_CLASS);
                document.body.style.removeProperty("top");
                document.body.style.removeProperty("padding-right");

                window.scrollTo(0, offsetY);
            }
        });
    }, [discriminator, dispatch, isLocked]);

    const toggle = useCallback(() => {
        (isLockedForDiscriminator() ? unlock : lock)();
    }, [isLockedForDiscriminator, unlock, lock]);

    return { lock, unlock, toggle, isLocked, isLockedForDiscriminator };
};

export { useScrollLock };
